import { z } from 'zod';

// User Role and Status enums
export type UserRole = 'user' | 'admin';
export type UserStatus = 'active' | 'blocked';

// User Action type
export type UserAction =
  | 'block'
  | 'unblock'
  | 'forceLogout'
  | 'terminateSubscription'
  | 'extendSubscription';

// Subscription types
export const SubscriptionStatusSchema = z.enum(['trial', 'active', 'canceled', 'expired', 'none']);
export type SubscriptionStatus = z.infer<typeof SubscriptionStatusSchema>;

export const SubscriptionPlanSchema = z.enum(['premium_monthly', 'premium_yearly']);
export type SubscriptionPlan = z.infer<typeof SubscriptionPlanSchema>;

export const SubscriptionStoreSchema = z.enum(['google_play', 'rustore']);
export type SubscriptionStore = z.infer<typeof SubscriptionStoreSchema>;

// Zod schemas for runtime validation
export const UserSchema = z.object({
  id: z.string().uuid(),
  email: z.string(),
  role: z.enum(['user', 'admin']),
  isActive: z.boolean(),
  isVerified: z.boolean(),
  createdAt: z.string().datetime(),
  updatedAt: z.string().datetime().optional(),
  // Subscription fields for list view
  subscriptionStatus: SubscriptionStatusSchema.optional().default('none'),
  subscriptionPlan: SubscriptionPlanSchema.optional().nullable(),
});

export const UsersResponseSchema = z.object({
  items: z.array(UserSchema),
  total: z.number().int().nonnegative(),
});

export const UserDetailResponseSchema = UserSchema.extend({
  emailVerifiedAt: z.string().datetime().optional().nullable(),
  nativeLanguage: z.string().optional().nullable(),
  onboardingCompleted: z.boolean(),
  onboardingCompletedAt: z.string().datetime().optional().nullable(),
  failedLoginAttempts: z.number().int().optional().default(0),
  lockedUntil: z.string().datetime().optional().nullable(),
  // Additional subscription fields for detail view
  subscriptionPeriodEnd: z.string().datetime().optional().nullable(),
  subscriptionCancelAtPeriodEnd: z.boolean().optional().default(false),
  subscriptionTrialUsed: z.boolean().optional().default(false),
  subscriptionStore: SubscriptionStoreSchema.optional().nullable(),
});

// TypeScript types inferred from Zod schemas
export type User = z.infer<typeof UserSchema>;
export type UsersResponse = z.infer<typeof UsersResponseSchema>;
export type UserDetailResponse = z.infer<typeof UserDetailResponseSchema>;

// User Filters interface
export interface UserFilters {
  search?: string;
  role?: UserRole | 'all';
  status?: 'active' | 'blocked' | 'all';
  sortBy?: 'createdAt';
  sortOrder?: 'asc' | 'desc';
  page?: number;
  limit?: number;
}

// Sort options type
export type SortBy = 'createdAt';
export type SortOrder = 'asc' | 'desc';
